import {
  Box,
  type BoxProps,
  IconButton,
  Tooltip,
  forwardRef,
  useMultiStyleConfig,
  useOutsideClick,
} from "@chakra-ui/react";
import { type ReactNode, useRef } from "react";

import { useDisclosure } from "@equidefi/hooks";

import { Icon } from "../../icon";
import { V2Theme } from "../../theme";

export interface DataSummaryCardProps extends BoxProps {
  variant?: "unknown" | "success" | "review" | "info" | "secondary" | "error";
  size?: "sm" | "md" | "lg";
  title: string;
  tooltip?: ReactNode;
  mainText: ReactNode;
  secondaryText?: ReactNode;
}

export const DataSummaryCard = forwardRef<DataSummaryCardProps, "div">((props, ref) => {
  const { variant = "unknown", title, mainText, secondaryText, tooltip, size = "md", onClick, ...rest } = props;

  const isClickable = !!onClick;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  useOutsideClick({ ref: toggleRef, handler: onClose, enabled: isOpen });

  const styles = useMultiStyleConfig("DataSummaryCard", {
    variant,
    size,
    isClickable,
    theme: V2Theme,
  });

  return (
    <Box __css={styles.container} {...rest} onClick={onClick}>
      {tooltip && (
        <Box __css={styles.tooltip}>
          <Tooltip label={tooltip} hasArrow isOpen={isOpen}>
            <IconButton
              __css={styles.tooltipButton}
              ref={toggleRef}
              h="auto"
              w="auto"
              variant="none"
              p={0}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              onMouseEnter={onOpen}
              onMouseLeave={onClose}
              onTouchStart={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              onTouchMove={onClose}
              size="sm"
              aria-label="Data info icon with tooltip"
              icon={<Icon.Info size="1em" />}
            />
          </Tooltip>
        </Box>
      )}
      <Box ref={ref} __css={styles.card}>
        <Box __css={styles.title}>{title}</Box>
        <Box __css={styles.mainText}>{mainText}</Box>
        {secondaryText && <Box __css={styles.secondaryText}>{secondaryText}</Box>}
      </Box>
    </Box>
  );
});

DataSummaryCard.displayName = "DataSummaryCard";
